import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invalidateCarbonCaches } from '@/lib/carbonCacheKeys';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/useToast';
import { convertValue, IncompatibleUnitsError } from '@releve/shared/units';
import type { SustainabilityActivityIndexRowWire } from '@releve/shared';
import { indexQueryOptions } from '@/hooks/lib/indexQueryOptions';

export interface SustainabilityActivity {
  id: string;
  organization_id: string;
  entry_type: 'activity' | 'transaction';
  /**
   * Origin discriminator. NULL = the classic activity/transaction capture
   * paths; 'place_energy' = projected from metered utility emissions
   * (place_utility_emissions). Projected rows are read-only in the UI —
   * they are overwritten wholesale on every engine recompute, so edits
   * belong on the meter readings, not here.
   */
  source: string | null;
  place_id: string | null;
  place_utility_account_id: string | null;
  ap_invoice_id?: string | null;
  activity_date: string | null;
  activity_description: string | null;
  quantity: number | null;
  quantity_unit: string | null;
  emission_table_id: string | null;
  emission_factor_id: string | null;
  dimension_values: Record<string, string> | null;
  emission_factor_value: number | null;
  multiplier_value: number | null;
  calculated_emissions: number;
  emission_scope: string | null;
  scope_3_category: string | null;
  project_id: string | null;
  location_id: string | null;
  account_id: string | null;
  transaction_id: string | null;
  created_by: string | null;
  created_at: string;
  calculation_date: string;
  // Joined data
  project?: {
    id: string;
    name: string;
  } | null;
  location?: {
    id: string;
    formatted_address: string;
  } | null;
  emission_table?: {
    id: string;
    table_name: string;
    table_code: string;
  } | null;
}

export interface CreateActivityInput {
  organizationId: string;
  activityDate: string;
  activityDescription: string;
  quantity: number;
  quantityUnit: string;
  /**
   * Canonical unit code the user picked (e.g., 'kg', 'night', 'short_ton').
   * When provided together with `factorInputUnitCode` the hook pre-converts
   * `quantity` to the factor's native input unit before the RPC call —
   * required for correct unit reconciliation. Null for legacy callers
   * (back-compat passthrough; the RPC will multiply against the raw value).
   */
  quantityUnitCode?: string | null;
  /**
   * The linked emission factor's `input_unit_code` (e.g., 't' for NL waste,
   * 'gallon' for EPA mobile). Captured by the form from
   * `useCompatibleMultiplierUnits`. Used together with `quantityUnitCode` to
   * pre-convert `quantity` via the `convert` library.
   */
  factorInputUnitCode?: string | null;
  emissionTableId: string;
  dimensionValues: Record<string, string>;
  projectId?: string | null;
  locationId?: string | null;
}

export interface UpdateActivityInput extends CreateActivityInput {
  activityId: string;
}

/** One row of the slim Impact index (the `sustainability_activity_index` DB view). */
export type SustainabilityActivityIndexRow = SustainabilityActivityIndexRowWire;

/**
 * The COMPLETE slim Impact index for an org — the API pages the
 * `sustainability_activity_index` view past PostgREST's 1,000-row cap, so
 * it is never silently truncated. Powers the Impact grid's client-side
 * search/sort/filter and the scope summary cards; heavy detail (dimension
 * values, attachments) is fetched lazily per row via
 * `useSustainabilityActivity` / `useActivityAttachments`.
 *
 * `options.projectId` scopes to one project via the view's
 * `effective_project_id` (COALESCE(cer.project_id, lt.project_id) — the
 * canonical get_carbon_summary link), serving the project Sustainability
 * tab from the same endpoint.
 *
 * Liveness: the key sits under the `['sustainability-activities', orgId]`
 * prefix. Invalidated by:
 *  - the activity CRUD mutations in this file;
 *  - `useCarbonTracking.saveCarbonEmissions` (expense/AP/AR form carbon);
 *  - `CreateTransactionModal`'s own `saveCarbonEmissions` (journal-line
 *    carbon). It invalidates from the component, not from the ledger
 *    mutation, because the carbon write happens AFTER the mutation settles —
 *    see the comment there;
 *  - the utilities mutations in `usePlaceUtilities.ts` (readings/account
 *    writes + `useRecalculateEmissions` rewrite the projected
 *    source='place_energy' rows) and the EmissionBindings settings patch;
 *  - the document mutations that cascade or reshape indexed fields:
 *    `useExpenses` (update/delete), `useLedgerTransactions`
 *    (update/delete), `useAccountsPayable` (approve/update/delete),
 *    `useAccountsReceivable` (update/delete).
 * `staleTime` only suppresses redundant mount/focus refetches between
 * mutations.
 */
export const useSustainabilityActivityIndex = (
  organizationId?: string,
  options?: { projectId?: string },
) => {
  const normalized = { projectId: options?.projectId ?? null };
  return useQuery(
    indexQueryOptions<SustainabilityActivityIndexRowWire>({
      entity: 'sustainability-activities',
      organizationId: organizationId,
      path: `/api/v1/sustainability/orgs/${organizationId}/activities/index`,
      params: normalized,
    }),
  );
};

/**
 * Hook to fetch a single sustainability activity
 */
export const useSustainabilityActivity = (activityId?: string) => {
  return useQuery({
    queryKey: ['sustainability-activity', activityId],
    queryFn: async () => {
      if (!activityId) return null;

      const { data, error } = await supabase
        .from('carbon_emission_results')
        .select(`
          *,
          project:projects(id, name),
          location:gis_locations(id, formatted_address),
          emission_table:carbon_emission_factor_tables(id, table_name, table_code)
        `)
        .eq('id', activityId)
        .eq('entry_type', 'activity')
        .single();

      if (error) throw error;

      return data as SustainabilityActivity;
    },
    enabled: !!activityId,
  });
};

/**
 * Hook to create a new sustainability activity
 */
export const useCreateSustainabilityActivity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateActivityInput) => {
      // Client owns the unit conversion; server owns the emission math.
      // Pre-convert the user's quantity to the factor's native input unit
      // when both codes are known. Legacy callers pass null → legacy
      // passthrough (server falls back to the raw quantity).
      const quantityNormalized =
        input.quantityUnitCode && input.factorInputUnitCode
          ? convertValue(input.quantity, input.quantityUnitCode, input.factorInputUnitCode)
          : null;

      const { data, error } = await supabase.rpc('create_sustainability_activity', {
        p_organization_id: input.organizationId,
        p_activity_date: input.activityDate,
        p_activity_description: input.activityDescription,
        p_quantity: input.quantity,
        p_quantity_unit: input.quantityUnit,
        p_emission_table_id: input.emissionTableId,
        p_dimension_values: input.dimensionValues,
        p_project_id: input.projectId || null,
        p_location_id: input.locationId || null,
        p_quantity_unit_code: input.quantityUnitCode ?? null,
        p_quantity_normalized: quantityNormalized,
      });

      if (error) throw error;

      // The RPC returns an array with one row
      return data?.[0] || data;
    },
    onSuccess: (_, variables) => {
      invalidateCarbonCaches(queryClient, variables.organizationId);
      toast.success('Sustainability activity logged successfully');
    },
    onError: (error: Error) => {
      if (error instanceof IncompatibleUnitsError) {
        toast.error(
          `Cannot convert ${error.from} to ${error.to} — the factor expects a different kind of unit. Pick a compatible unit or adjust the factor's input unit in Settings.`,
        );
      } else {
        toast.errorTemplates.create('sustainability activity');
      }
    },
  });
};

/**
 * Hook to update an existing sustainability activity
 */
export const useUpdateSustainabilityActivity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateActivityInput) => {
      const quantityNormalized =
        input.quantityUnitCode && input.factorInputUnitCode
          ? convertValue(input.quantity, input.quantityUnitCode, input.factorInputUnitCode)
          : null;

      const { data, error } = await supabase.rpc('update_sustainability_activity', {
        p_activity_id: input.activityId,
        p_activity_date: input.activityDate,
        p_activity_description: input.activityDescription,
        p_quantity: input.quantity,
        p_quantity_unit: input.quantityUnit,
        p_emission_table_id: input.emissionTableId,
        p_dimension_values: input.dimensionValues,
        p_project_id: input.projectId || null,
        p_location_id: input.locationId || null,
        p_quantity_unit_code: input.quantityUnitCode ?? null,
        p_quantity_normalized: quantityNormalized,
      });

      if (error) throw error;

      return data?.[0] || data;
    },
    onSuccess: (_, variables) => {
      invalidateCarbonCaches(queryClient, variables.organizationId);
      queryClient.invalidateQueries({
        queryKey: ['sustainability-activity', variables.activityId],
      });
      toast.success('Sustainability activity updated successfully');
    },
    onError: (error: Error) => {
      if (error instanceof IncompatibleUnitsError) {
        toast.error(
          `Cannot convert ${error.from} to ${error.to} — the factor expects a different kind of unit. Pick a compatible unit or adjust the factor's input unit in Settings.`,
        );
      } else {
        toast.errorTemplates.update('sustainability activity');
      }
    },
  });
};

/**
 * Hook to delete a sustainability activity
 */
export const useDeleteSustainabilityActivity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      activityId,
      organizationId,
    }: {
      activityId: string;
      organizationId: string;
    }) => {
      const { data, error } = await supabase.rpc('delete_sustainability_activity', {
        p_activity_id: activityId,
      });

      if (error) throw error;

      return data;
    },
    onSuccess: (_, variables) => {
      invalidateCarbonCaches(queryClient, variables.organizationId);
      toast.success('Sustainability activity deleted');
    },
    onError: () => {
      toast.errorTemplates.delete('sustainability activity');
    },
  });
};

